/**
 * File: accountingTutorialsData.ts
 * Path: apps/admin/src/shared/utils/accountingTutorialsData.ts
 * Description: The tutorials for the accounting pages (Overview, Transactions,
 * Accounts, Budgets, Reports, Tax and filings, Setup) and for the Money build's money
 * jobs (credit notes, reminders, statements). Each walks one job in 1 to 5
 * short steps and names the buttons exactly as they read on screen ("Post to
 * ledger", Liam's choice of 18 October 2026).
 */

import { ADMIN_PATHS } from "@idk/config";
import { HEAD, PAGE_ACTIONS, type TourStep, type Tutorial } from "./tutorialTypes";

const A = ADMIN_PATHS.accounting;
const INVOICE = ADMIN_PATHS.invoice(":key");
const CLIENT = ADMIN_PATHS.businesses.detail(":id");

const UPLOAD: TourStep = {
  path: A.journals,
  target: '[data-tour="upload-statement"]',
  title: "Upload statement",
  text: "Press Upload statement and choose the PDF, CSV or OFX file from your bank. A file that does not add up to the bank's balance is refused, so nothing wrong gets in.",
};
const BANK_FIGURES: TourStep = {
  path: A.journals,
  target: '[aria-label="The bank account at a glance"]',
  title: "Bank and books",
  text: "These boxes compare the bank with your books. When Difference is nil, they agree. To sort counts the bank lines still waiting for an account.",
};
const SORT_TAB: TourStep = {
  path: A.journals,
  target: "#tx-tab-sort",
  title: "To sort",
  text: "Open To sort. Every bank line that is not in the books yet waits here, newest first.",
};
const BANK_LINES: TourStep = {
  path: A.journals,
  target: '[aria-label="Bank lines"]',
  title: "Post to ledger",
  text: "Check the account in the line's box. Press Post to ledger. The books write the debit and credit and remember your choice for next time.",
};
const REGISTER: TourStep = {
  path: A.journals,
  target: "#tx-tab-register",
  title: "Transactions",
  text: "Open Transactions. Search by name or word, or filter by account, client or dates. Press an entry to see its debits, credits and receipts.",
};
const RECORD = (choice: string, what: string): TourStep => ({
  path: A.overview,
  target: '[data-tour="record-something"]',
  title: "Record something",
  text: `Press Record something and choose ${choice}. Fill in the date, ${what} and the amount. Add a photo of the slip if you have one. Press Record it.`,
});
const FIGURES: TourStep = {
  target: '[aria-label="Key figures"]',
  title: "Your figures",
  text: "The main numbers for the dates you picked. Cash in the bank is what you can spend. You owe is what the business must pay.",
};
const SETUP_PROGRESS: TourStep = {
  path: A.setup,
  target: '[aria-label="Setup progress"]',
  title: "Setup progress",
  text: "The steps to get these books ready. A tick means the step is done.",
};

const OVERVIEW: Tutorial[] = [
  {
    id: "overview-figures",
    title: "Read your figures",
    hint: "1 step",
    pages: [A.overview],
    group: "Accounting: Overview",
    steps: [FIGURES],
  },
  {
    id: "money-in",
    title: "Record money that came in",
    hint: "2 steps",
    pages: [A.overview],
    group: "Accounting: Overview",
    guideId: "money-in",
    steps: [
      RECORD("Money came in", "who paid and what for"),
      { ...FIGURES, text: "The entry is posted to ledger straight away, with the debits and credits done for you. Your figures here update." },
    ],
  },
  {
    id: "money-out",
    title: "Record something you paid for",
    hint: "2 steps",
    pages: [A.overview],
    group: "Accounting: Overview",
    guideId: "money-out",
    steps: [
      RECORD("I paid for something", "what it was for, what you paid with"),
      { ...FIGURES, text: "Paid with your own money? Tick that box, and the business owes you. The entry is posted to ledger straight away." },
    ],
  },
  {
    id: "overview-attention",
    title: "Deal with Needs attention",
    hint: "1 step",
    pages: [A.overview],
    group: "Accounting: Overview",
    steps: [
      {
        target: '[aria-label="Needs attention"]',
        title: "Needs attention",
        text: "A short list of things to look at, like lines to sort or a bill that is late. Open one to go straight to it.",
      },
    ],
  },
];

const TRANSACTIONS: Tutorial[] = [
  {
    id: "sort-bank-lines",
    title: "Upload a bank statement",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    guideId: "bank",
    steps: [UPLOAD, BANK_FIGURES],
  },
  {
    id: "post-bank-line",
    title: "Post a bank line to ledger",
    hint: "3 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      SORT_TAB,
      BANK_LINES,
      {
        ...BANK_LINES,
        title: "Many at once",
        text: "Lines a rule is sure about count as usual. Open Actions and press Post N usual lines to ledger. Or tick lines and press Post N to ledger.",
      },
    ],
  },
  {
    id: "match-payment",
    title: "Match a client payment to its invoice",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      SORT_TAB,
      {
        ...BANK_LINES,
        title: "Match and post",
        text: "When a line looks like a client's payment, its box shows Matches INV-… Press Match and post. The invoice is paid and the line is posted to ledger. Pick an account instead to post it there.",
      },
    ],
  },
  {
    id: "split-line",
    title: "Split a line between accounts",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      SORT_TAB,
      {
        ...BANK_LINES,
        title: "Split it",
        text: "Open the line's ⋮ menu and choose Split between accounts. Give each part an amount and an account. When the parts add up, press Post the split to ledger.",
      },
    ],
  },
  {
    id: "combine-lines",
    title: "Combine lines into one entry",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      SORT_TAB,
      {
        ...BANK_LINES,
        title: "Combine them",
        text: "Tick the lines that belong together. Open Actions and choose Combine into one entry. Name it, pick the account and press Post as one entry.",
      },
    ],
  },
  {
    id: "ignore-line",
    title: "Ignore a line already in the books",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      SORT_TAB,
      {
        ...BANK_LINES,
        title: "Ignore it",
        text: "Open the line's ⋮ menu and choose Ignore. It never reaches the books. Changed your mind? Use Filter, then Ignored, and press Bring back.",
      },
    ],
  },
  {
    id: "check-statement",
    title: "Check the books against a statement",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      {
        path: A.journals,
        target: "#tx-tab-reconcile",
        title: "Check against statement",
        text: "Open Check against statement and pick the statement. Or type its last date and the bank's closing balance.",
      },
      {
        path: A.journals,
        target: '[aria-label="Entries to check"]',
        title: "Tick and agree",
        text: "Tick the entries on the statement until Difference is nil, then press Agree. Agree and lock in Actions also locks those months.",
      },
    ],
  },
  {
    id: "undo-entry",
    title: "Undo an entry",
    hint: "2 steps",
    pages: [A.journals, A.accounts],
    group: "Accounting: Transactions",
    steps: [
      REGISTER,
      {
        ...REGISTER,
        title: "Undo entry",
        text: "Open the entry's ⋮ menu and choose Undo entry. Say why and press Undo it. It adds an opposite entry. Nothing is deleted.",
      },
    ],
  },
  {
    id: "make-rule",
    title: "Make a rule",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      {
        path: A.journals,
        target: "#tx-tab-rules",
        title: "Rules",
        text: "Open Rules and press New rule. Or use Save as a rule in a bank line's ⋮ menu.",
      },
      {
        path: A.journals,
        target: "#tx-tab-rules",
        title: "Words and account",
        text: "Type a word the bank line contains and pick its account. Tick Count it as usual, so such lines post with Post N usual lines to ledger.",
      },
    ],
  },
  {
    id: "add-receipt",
    title: "Add a receipt to an entry",
    hint: "1 step",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      {
        path: A.journals,
        target: "#tx-tab-files",
        title: "Files",
        text: "Open Files and press Add file. Choose the entry, then the receipt or invoice. The amounts never change. Without a receipt lists the spending still missing one.",
      },
    ],
  },
  {
    id: "transfer",
    title: "Move money between your accounts",
    hint: "1 step",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      {
        path: A.journals,
        target: PAGE_ACTIONS,
        title: "Transfer between accounts",
        text: "Open the menu at the top and choose Transfer between accounts. Pick the date, where it left, where it went and the amount. Press Post the transfer to ledger.",
      },
    ],
  },
  {
    id: "manual-entry",
    title: "Make a manual entry",
    hint: "2 steps",
    pages: [A.journals],
    group: "Accounting: Transactions",
    steps: [
      {
        path: A.journals,
        target: PAGE_ACTIONS,
        title: "Manual entry",
        text: "Open the menu at the top and choose Manual entry (debits and credits). Pick the date and write a note.",
      },
      {
        path: A.journals,
        target: PAGE_ACTIONS,
        title: "Debits and credits",
        text: "Add a line for each account, with its debit or credit. When Difference shows Balanced, press Post to ledger. Once posted, an entry can't be edited. To fix one, undo it.",
      },
    ],
  },
  {
    id: "find-entry",
    title: "Find an old entry",
    hint: "1 step",
    pages: [A.journals],
    group: "Accounting: Transactions",
    guideId: "find",
    steps: [REGISTER],
  },
];

const ACCOUNTS: Tutorial[] = [
  {
    id: "add-account",
    title: "Add an account",
    hint: "1 step",
    pages: [A.accounts],
    group: "Accounting: Accounts",
    steps: [
      {
        path: A.accounts,
        target: "#ac-tab-chart",
        title: "New account",
        text: "Open Chart of accounts and press New account. Give it a code, a name and its type: asset, liability, equity, income or expense.",
      },
    ],
  },
  {
    id: "accounts-history",
    title: "Read an account's history",
    hint: "1 step",
    pages: [A.accounts],
    group: "Accounting: Accounts",
    steps: [
      {
        target: '[role="tablist"][aria-label="Accounts"]',
        title: "Accounts",
        text: "Every account the books use. Press one to open its page: every entry that touched it, and its balance on any day.",
      },
    ],
  },
  {
    id: "fix-suspense",
    title: "Fix something in Suspense",
    hint: "2 steps",
    pages: [A.accounts],
    group: "Accounting: Accounts",
    steps: [
      {
        path: A.accounts,
        target: '[aria-label="Account figures"]',
        title: "Not sure yet (Suspense)",
        text: "The Suspense box shows money posted to Not sure yet (Suspense), waiting for a proper account. Open that account in the chart to see each entry.",
      },
      {
        path: A.journals,
        target: PAGE_ACTIONS,
        title: "Move it out",
        text: "Make a manual entry: debit the right account and credit Not sure yet (Suspense), or the other way for money in. Press Post to ledger.",
      },
    ],
  },
];

const REPORTS: Tutorial[] = [
  {
    id: "reports-dates",
    title: "Pick dates and compare",
    hint: "1 step",
    pages: [A.reports],
    group: "Accounting: Reports",
    steps: [
      {
        target: HEAD,
        title: "Dates and Compare with",
        text: "Pick the dates at the top. On P&L and Balance sheet, Compare with puts the same dates a year earlier, the period before, or dates you pick beside them.",
      },
    ],
  },
  {
    id: "reports-download",
    title: "Download a report",
    hint: "1 step",
    pages: [A.reports],
    group: "Accounting: Reports",
    steps: [
      {
        target: '[role="tablist"][aria-label="Reports"]',
        title: "Reports",
        text: "P&L shows what you earned and spent. Balance sheet shows what you own and owe. Pick a tab, then press Download for a PDF or a spreadsheet.",
      },
    ],
  },
  {
    id: "aged-debtors",
    title: "Read aged debtors",
    hint: "1 step",
    pages: [A.reports],
    group: "Accounting: Reports",
    steps: [
      {
        path: A.reports,
        target: "#rp-tab-debtors",
        title: "Aged debtors",
        text: "Open Aged debtors. Each client's unpaid invoices sit in columns by how late they are. The oldest money is the hardest to collect, so chase it first.",
      },
    ],
  },
];

const BUDGETS: Tutorial[] = [
  {
    id: "budget-set",
    title: "Set a budget",
    hint: "4 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: PAGE_ACTIONS,
        title: "Edit budget",
        text: "Press Edit budget. It opens Set the budget for the year shown under Budget. Pick another year there if you need to.",
      },
      {
        path: A.budgets,
        target: '[data-tour="budget-fill"]',
        title: "Fill the year",
        text: "Select the rows to fill. Pick Last year's actuals, Average of last 3 months, Same amount each month or Blank. Choose Adjust by for a % up or down. Then press Fill selected rows.",
      },
      {
        path: A.budgets,
        target: '[data-tour="budget-grid"]',
        title: "Type any month",
        text: "Type over any month in rand. Add an account below the grid, or press the cross to remove one. The year totals and budgeted net profit update as you type.",
      },
      {
        path: A.budgets,
        target: '[data-tour="budget-save"]',
        title: "Save budget",
        text: "Press Save budget. Each save is a new version and the old ones are kept, so you can always see what changed.",
      },
    ],
  },
  {
    id: "budget-read",
    title: "Read budget vs actual",
    hint: "3 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: "#bg-tab-actual",
        title: "Budget vs actual",
        text: "Open Budget vs actual. Pick the Period: the year so far, a month, a quarter or the full year.",
      },
      {
        path: A.budgets,
        target: '[data-tour="budget-table"]',
        title: "F and U",
        text: "F is favourable: income above budget or costs below it, in green. U is unfavourable, in red. The bar shows how much of the budget is used; the line marks 100%. Click an account to open its ledger.",
      },
      {
        path: A.budgets,
        target: '[data-tour="budget-period"]',
        title: "Download",
        text: "Press Download beside the Period for a PDF on the letterhead, or Excel or CSV.",
      },
    ],
  },
  {
    id: "budget-forecast",
    title: "Change a forecast method",
    hint: "2 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: "#bg-tab-forecast",
        title: "Forecast",
        text: "Open Forecast. The chart shows costs by month: posted months solid, the months to come dashed, and the budget as a line.",
      },
      {
        path: A.budgets,
        target: '[data-tour="forecast-methods"]',
        title: "Method",
        text: "Each account shows what its forecast is worked out from. Change Method to Same month last year, Budget for the rest or Average of the last 3 months. It is saved at once.",
      },
    ],
  },
  {
    id: "recurring-add",
    title: "Add a recurring payment",
    hint: "3 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: "#bg-tab-recurring",
        title: "Recurring payments",
        text: "Open Recurring payments. It lists every bill that repeats, like domains, hosting and software, with its next due date.",
      },
      {
        path: A.budgets,
        target: PAGE_ACTIONS,
        title: "Add payment",
        text: "Press Add payment. Type the name, the supplier, the amount and the next due date. Pick Every year or Every month and the expense account. Press Save.",
      },
      {
        path: A.budgets,
        target: '[data-tour="recurring-found"]',
        title: "Found in your books",
        text: "Payments your books show repeating wait under Found in your books. Press Add to track one; the form comes filled in and you type the name.",
      },
    ],
  },
  {
    id: "recurring-paid",
    title: "Mark a recurring payment paid",
    hint: "2 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: '[data-tour="recurring-table"]',
        title: "Paid by itself",
        text: "Post the bank line as usual. When it names the supplier and the amount is close, the payment is marked paid and the next due date moves on.",
      },
      {
        path: A.budgets,
        target: '[data-tour="recurring-table"]',
        title: "Mark paid",
        text: "Paid some other way? Press the three dots on its row and choose Mark paid. Pick the day you paid and press Mark paid.",
      },
    ],
  },
  {
    id: "recurring-reminder",
    title: "When the reminder comes",
    hint: "2 steps",
    pages: [A.budgets],
    group: "Accounting: Budgets",
    steps: [
      {
        path: A.budgets,
        target: '[aria-label="Recurring payment figures"]',
        title: "Next reminder",
        text: "The push comes at 7am on the 1st of the latest month that is still at least 14 days before the due date. A bill due 19 Dec is reminded on 1 Dec.",
      },
      {
        path: A.budgets,
        target: '[data-tour="recurring-table"]',
        title: "Still to pay",
        text: "A second push comes 3 days before the due date, but only if it is not marked paid. Monthly card debits stay quiet unless you switch Remind me on.",
      },
    ],
  },
];

const TAX: Tutorial[] = [
  {
    id: "tax-due",
    title: "See which taxes are due",
    hint: "2 steps",
    pages: [A.tax],
    group: "Accounting: Tax and filings",
    steps: [
      {
        target: '[aria-label="Needs attention"]',
        title: "Needs attention",
        text: "The tax jobs due soon or late, most urgent first. Open one to see what to do and by when.",
      },
      {
        target: '[aria-label="Your tax filings"]',
        title: "Your tax filings",
        text: "One row for each tax, with its due date. Press a row to open that tax.",
      },
    ],
  },
  {
    id: "income-tax-return",
    title: "File income tax",
    hint: "3 steps",
    pages: [A.taxIncome],
    group: "Accounting: Tax and filings",
    steps: [
      {
        path: A.taxIncome,
        target: "#it-tab-records",
        title: "Records",
        text: "Start with Records. Fill in what the books cannot know, like your SIC code and the personal-service share.",
      },
      {
        path: A.taxIncome,
        target: "#it-tab-return",
        title: "Return",
        text: "Return lists each line of the form with its figure. Copy them into SARS eFiling. Working shows how each one was worked out.",
      },
      {
        path: A.taxIncome,
        target: "#it-tab-working",
        title: "Mark as filed",
        text: "Once you submit on eFiling, press Mark as filed. That stops the phone reminders.",
      },
    ],
  },
  {
    id: "add-equipment",
    title: "Add equipment",
    hint: "1 step",
    pages: [A.taxIncome],
    group: "Accounting: Tax and filings",
    steps: [
      {
        path: A.taxIncome,
        target: '[aria-label="Equipment list"]',
        title: "Equipment list",
        text: "Equipment posted to the equipment accounts is listed for you. Press Add equipment only for what the books do not show. Its wear and tear claim goes on the return.",
      },
    ],
  },
  {
    id: "provisional-payments",
    title: "Record a provisional payment",
    hint: "1 step",
    pages: [A.taxProvisional],
    group: "Accounting: Tax and filings",
    steps: [
      {
        target: '[aria-label="Provisional payments"]',
        title: "Provisional payments",
        text: "Pay on eFiling first. Then press Record payment on that row. Tick Post the payment to ledger from a bank account and pick the bank.",
      },
    ],
  },
  {
    id: "other-taxes",
    title: "Check the other taxes",
    hint: "1 step",
    pages: [A.taxOther],
    group: "Accounting: Tax and filings",
    steps: [
      {
        target: HEAD,
        title: "Other taxes",
        text: "VAT, PAYE, dividends tax and the tax on a director's loan. Each shows whether it applies to you and when it is due.",
      },
    ],
  },
  {
    id: "annual-return",
    title: "File the CIPC annual return",
    hint: "1 step",
    pages: [A.taxCompany],
    group: "Accounting: Tax and filings",
    steps: [
      {
        target: '[aria-label="This annual return"]',
        title: "Annual return",
        text: "When it is due, the fee and the details that go on it. Follow the steps on CIPC's site. The company's details live in Settings, Company.",
      },
    ],
  },
  {
    id: "bo-documents",
    title: "Make the beneficial ownership documents",
    hint: "1 step",
    pages: [A.taxCompany],
    group: "Accounting: Tax and filings",
    steps: [
      {
        target: '[aria-label="Documents for CIPC"]',
        title: "Documents for CIPC",
        text: "Press Mandate, Securities register, Beneficial interest register or Disclosure form for a PDF to upload to CIPC. Sign where marked. Add the shares issued in Settings, Company first.",
      },
    ],
  },
];

const SETUP: Tutorial[] = [
  {
    id: "setup-steps",
    title: "Check the setup steps",
    hint: "1 step",
    pages: [A.setup],
    group: "Accounting: Setup",
    steps: [SETUP_PROGRESS],
  },
  {
    id: "first-day",
    title: "Set the first day of the books",
    hint: "1 step",
    pages: [A.setup],
    group: "Accounting: Setup",
    steps: [
      {
        path: A.setup,
        target: "#setup-tab-books",
        title: "Books",
        text: "Books shows the first day and the year end. Start the books earlier moves the first day back, before any entry is dated.",
      },
    ],
  },
  {
    id: "opening-balances",
    title: "Post opening balances to ledger",
    hint: "1 step",
    pages: [A.setup],
    group: "Accounting: Setup",
    steps: [
      {
        path: A.setup,
        target: "#setup-tab-opening",
        title: "Opening balances",
        text: "Open Opening balances and choose Zoho's trial balance for the day before the books start. Match each account, then press Post opening balances to ledger.",
      },
    ],
  },
  {
    id: "zoho-history",
    title: "Bring in your Zoho history",
    hint: "2 steps",
    pages: [A.setup],
    group: "Accounting: Setup",
    guideId: "history",
    steps: [
      {
        path: A.setup,
        target: "#setup-tab-import",
        title: "Import from Zoho",
        text: "Open Import from Zoho. Choose the CSV files you exported from Zoho Books. You see every entry first, and nothing is posted yet.",
      },
      {
        path: A.setup,
        target: '[aria-label="Entries to post"]',
        title: "Post to ledger",
        text: "Read the table, then press Post N entries to ledger. Pressing it again later only adds what is new.",
      },
    ],
  },
  {
    id: "link-clients",
    title: "Link old entries to clients",
    hint: "1 step",
    pages: [A.setup],
    group: "Accounting: Setup",
    steps: [
      {
        path: A.setup,
        target: '[data-tour="link-clients"]',
        title: "Link to clients and jobs",
        text: "Press Check the links to see which client each old entry belongs to. Read the list, then press Link N journals, so each client's page shows their money.",
      },
    ],
  },
  {
    id: "close-month",
    title: "Lock a month",
    hint: "2 steps",
    pages: [A.reports, A.setup],
    group: "Accounting: Setup",
    guideId: "month",
    steps: [
      {
        path: A.reports,
        target: '[role="tablist"][aria-label="Reports"]',
        title: "Check the numbers",
        text: "Look at the month's P&L first. If it looks right, go on and lock it.",
      },
      {
        path: A.setup,
        target: "#setup-tab-lock",
        title: "Lock months",
        text: "Open Lock months. Under Month lock, pick the month's last day and press Lock. A locked month can't change by accident.",
      },
    ],
  },
  {
    id: "compare-zoho",
    title: "Compare with Zoho at month end",
    hint: "1 step",
    pages: [A.setup],
    group: "Accounting: Setup",
    steps: [
      {
        path: A.setup,
        target: "#setup-tab-compare",
        title: "Compare with Zoho",
        text: "Open Compare with Zoho and pick the month end. Choose Zoho's reports for that day. Each check shows whether the two agree, and you note why where they don't.",
      },
    ],
  },
];

const MONEY: Tutorial[] = [
  {
    id: "invoice-credit-note",
    title: "Issue a credit note",
    hint: "1 step",
    pages: [INVOICE],
    group: "CRM: Money",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Credit note",
        text: "Open the ⋮ menu beside the main button and choose Credit note. Type the amount and why, then press Issue credit note. It is posted to ledger and lowers what they owe.",
      },
    ],
  },
  {
    id: "remind-client",
    title: "Remind a client to pay",
    hint: "1 step",
    pages: [ADMIN_PATHS.money],
    group: "CRM: Money",
    steps: [
      {
        target: '[aria-label="Money lists"]',
        title: "Remind on WhatsApp",
        text: "Open Still to bill. On a late payment's ⋮ menu, choose Remind on WhatsApp. The message opens ready in WhatsApp. The date you reminded them is kept.",
      },
    ],
  },
  {
    id: "client-statement",
    title: "Send a client their statement",
    hint: "1 step",
    pages: [CLIENT],
    group: "CRM: Money",
    steps: [
      {
        target: '[data-tour="client-statement"]',
        title: "Statement",
        text: "Pick the dates. The statement reads every invoice and payment from the books. Press Send to email or WhatsApp it, or Download for the PDF.",
      },
    ],
  },
];

export const ACCOUNTING_TUTORIALS: Tutorial[] = [...OVERVIEW, ...TRANSACTIONS, ...ACCOUNTS, ...BUDGETS, ...REPORTS, ...TAX, ...SETUP, ...MONEY];
